import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Row, Col, Form, Accordion } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { fetchJobSchedule } from '../../redux/slices/jobScheduleSlice';
import { postData } from '../../redux/slices/apiCallSlice';
import SaveBanner from '../JobDetailPage/Mechanical/SaveBanner';
import EngineerAssignmentModal from './EngineerAssignmentModal';
import EngineeringRequirementsNotice from './EngineeringRequirementsNotice';
import useScheduleChangeLog from '../../functions/useScheduleChangeLog';
// Import centralized date utilities
import { 
  formatDateForForm, 
  isValidDateFormat
} from '../../utils/dateUtils';
// Import permission utilities
import { 
  canEditField
} from './utils/schedulePermissions';

// Helper function for text processing
const separateCamelCase = (text) => {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Separate camel case
    .replace(/(HC|VT|NP|CSM|HDPE)/g, ' $1 ') // Treat specific abbreviations as single words
    .replace(/\s+/g, ' ') // Remove extra spaces
    .trim();
};

const NewJobModal = ({ handleCloseModal, moJobData, canEdit = true, userRoles = {} }) => {
  // Fix: Properly access the apiUrls from the nested structure in the Redux store
  const apiUrls = useSelector((state) => state.apiUrls.urls);
  const dispatch = useDispatch();
  const options = useSelector((state) => state.dropdown.options); // Get dropdown options from Redux
    // Get user dropdowns from Redux store
  const userDropdowns = useSelector((state) => state.dropdown.userDropdowns);
  const mechOptions = userDropdowns.mechOptions || [];
  const elecOptions = userDropdowns.elecOptions || [];
  const controlOptions = userDropdowns.controlOptions || [];
  const salesOptions = userDropdowns.salesOptions || []; // Add sales options
  
  // Get schedule change logging functionality
  const { logScheduleChange } = useScheduleChangeLog();

  // State for engineer assignment modal
  const [showEngineerModal, setShowEngineerModal] = useState(false);

  // State to track if changes were made
  const [changesMade, setChangesMade] = useState(false);
  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  
  // State for engineering requirements when coming from MO search
  const [engineeringRequirements, setEngineeringRequirements] = useState(null);
    // Function to reset form to default values
  const resetForm = () => {
    // Reset to the same default values as in the initial state
    setNewJob({
      JobNumber: '',
      ProjectName: '',
      ModelNumber: '',
      JobStatus: 'Ready to Schedule',
      JobType: '',
      SalesRep: '',
      JobLocation: '',
      Notes: '',
      scheduleOnly: 'Yes',
      productionRelease: new Date().toISOString().split('T')[0],
      scheduledDate: new Date().toISOString().split('T')[0],
      DollarValue: '',
      PaymentTerms: '',
      sendDocumentsToSales: 'false',
      customControlsEngineering: 'false',
      newConstruction: 'false',    hasSubmittal: 'false',
    });
    
    // Reset engineering requirements
    setEngineeringRequirements(null);
    
    // Reset changes made flag
    setChangesMade(false);
  };

  // Initialize a new job with default values
  const [newJob, setNewJob] = useState({
    JobNumber: '',
    ProjectName: '',
    ModelNumber: '',    JobStatus: 'Ready to Schedule',
    JobType: '',
    SalesRep: '',
    JobLocation: '',
    Notes: '',
    scheduleOnly: 'Yes', // Set scheduleOnly to 'Yes' by default (camelCase)
    productionRelease: new Date().toISOString().split('T')[0], // Initialize ProductionRelease as an empty string
    scheduledDate: new Date().toISOString().split('T')[0], // Set scheduledDate to today's date dynamically (camelCase)
    DollarValue: '', // Initialize DollarValue as an empty string
    PaymentTerms: '', // Initialize PaymentTerms as an empty string
    // Set default values for required boolean fields as strings with camelCase
    sendDocumentsToSales: 'false',
    customControlsEngineering: 'false',
    newConstruction: 'false',
    hasSubmittal: 'false',
  });
  // Effect to populate data from MO search if available
  useEffect(() => {
    if (moJobData) {
      setNewJob(prevJob => ({
        ...prevJob,
        ...moJobData
      }));
      
      // If this job was created from MO search and has engineering requirements,
      // set them for the notice component
      if (moJobData.fromMOSearch) {
        setEngineeringRequirements({
          MechanicalEngineering: moJobData.MechanicalEngineering || '',
          ElectricalEngineering: moJobData.ElectricalEngineering || '',
          ProgrammingRequired: moJobData.ProgrammingRequired || ''
        });
      }
    }  }, [moJobData]);

  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for DollarValue to prevent NaN formatting issues
    if (name === 'DollarValue') {
      // Remove any non-numeric characters except decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      setNewJob(prevJob => ({
        ...prevJob,
        [name]: numericValue
      }));
    } else {
      // Update original ship date if current plan ship date is being changed and original is empty
      if (name === 'CurrentPlanShipDate' && (!newJob.OriginalScheduledShipDate || newJob.OriginalScheduledShipDate === '')) {
        setNewJob(prevJob => ({
          ...prevJob,
          [name]: value,
          OriginalScheduledShipDate: value // Set original to match current if original is empty
        }));
      } else {
        setNewJob(prevJob => ({
          ...prevJob,
          [name]: value
        }));
      }
    }
    
    // Mark that changes have been made
    setChangesMade(true);
  };
  const handleSave = async () => {
    try {
      // Build GeneralProject payload using PascalCase keys
      const jobDataForApi = {
        JobNumber: newJob.JobNumber,
        ProjectName: newJob.ProjectName,
        ModelNumber: newJob.ModelNumber,
        JobStatus: newJob.JobStatus,
        JobType: newJob.JobType,
        SalesRep: newJob.SalesRep,
        JobLocation: newJob.JobLocation,
        Notes: newJob.Notes,
        ScheduleOnly: newJob.scheduleOnly,
        ScheduledDate: newJob.scheduledDate,
        // Pass boolean fields as strings as required by the API
        SendDocumentsToSales: newJob.sendDocumentsToSales,
        CustomControlsEngineering: newJob.customControlsEngineering,
        NewConstruction: newJob.newConstruction,
        HasSubmittal: newJob.hasSubmittal,
        // Include date and schedule fields
        OrderDate: newJob.OrderDate,
        ProductionRelease: newJob.ProductionRelease ?? newJob.productionRelease,
        MechanicalDue: newJob.MechanicalDue,
        ElectricalDue: newJob.ElectricalDue,
        ControlsDue: newJob.ControlsDue,
        EngineeringDue: newJob.EngineeringDue,
        NPPipeFabDue: newJob.NPPipeFabDue,
        FabDue: newJob.FabDue,
        SoftwareDue: newJob.SoftwareDue,
        ControlPanelDue: newJob.ControlPanelDue,
        HCPumpsDue: newJob.HCPumpsDue,
        VTPumpsDue: newJob.VTPumpsDue,
        AssemblyDue: newJob.AssemblyDue,
        TestDue: newJob.TestDue,
        NPCSMDue: newJob.NPCSMDue,
        NPSuctionLineDue: newJob.NPSuctionLineDue,
        NPInducerTubeDue: newJob.NPInducerTubeDue,
        NPBMXDue: newJob.NPBMXDue,
        NPFertigationDue: newJob.NPFertigationDue,
        PackDue: newJob.PackDue,
        TransportType: newJob.TransportType,
        OriginalScheduledShipDate: newJob.OriginalScheduledShipDate,
        CurrentPlanShipDate: newJob.CurrentPlanShipDate,
        // Ensure dollar value is passed as a string
        DollarValue: newJob.DollarValue ? newJob.DollarValue.toString().replace(/[^0-9.-]/g, '') : '',
        PaymentTerms: newJob.PaymentTerms,
        MechanicalEngineer: newJob.MechanicalEngineer,
        ElectricalEngineer: newJob.ElectricalEngineer,
        ControlsEngineer: newJob.ControlsEngineer,
        MechanicalDuration: newJob.MechanicalDuration,
        ElectricalDuration: newJob.ElectricalDuration,
        ControlsDuration: newJob.ControlsDuration
      };
      
      // First: Make POST request to create a new job with retry logic
      const responseData = await dispatch(
        postData({
          url: apiUrls.generalProject,
          data: jobDataForApi,
          contract: { requestCase: 'pascal' },
        })
      ).unwrap();
      
      // Only proceed with logging if the main API call succeeded
      if (responseData) {
        console.log('New job created successfully:', responseData);
        
        // Get the new job ID from the response
        const newJobId = responseData?.id || responseData?.ID;
        
        // Second: Log the job creation and field values (only if main creation succeeded)
        if (newJobId) {
          try {
            // Log the job creation action
            await logScheduleChange(
              newJobId,
              newJob.JobNumber || '',
              newJob.ProjectName || '',
              'Job Creation',
              '',
              `Job ${newJob.JobNumber} created with status ${newJob.JobStatus}`
            );
              // Log individual field values for the new job
            const fieldsToLog = [
              'JobNumber', 'ProjectName', 'ModelNumber', 'JobStatus', 'JobType',
              'SalesRep', 'JobLocation', 'OrderDate', 'ProductionRelease',
              'EngineeringDue', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlsEngineer',
              'MechanicalDuration', 'ElectricalDuration', 'ControlsDuration',
              'NPPipeFabDue', 'FabDue', 'SoftwareDue', 'ControlPanelDue',
              'HCPumpsDue', 'VTPumpsDue', 'AssemblyDue', 'TestDue',
              'PackDue', 'OriginalScheduledShipDate', 'CurrentPlanShipDate',
              'TransportType', 'DollarValue', 'PaymentTerms', 'Notes'
            ];
            
            for (const field of fieldsToLog) {
              if (newJob[field]) {
                await logScheduleChange(
                  newJobId,
                  newJob.JobNumber || '',
                  newJob.ProjectName || '',
                  field,
                  '',
                  newJob[field]
                );
              }
            }
            console.log(`Successfully logged creation of job ${newJob.JobNumber}`);
          } catch (logError) {
            console.warn('Job creation succeeded but some change logging failed:', logError);
            // Don't throw here - the main creation worked
          }
        }
        
        // Third: Update Redux and show success (only if main creation succeeded)
        dispatch(fetchJobSchedule());
        
        // Show success notification
        setSaveStatus("Successfully created new job");
        setShowSaveBanner(true);
        
        // Hide the notification after 3 seconds
        setTimeout(() => {
          setShowSaveBanner(false);
        }, 3000);
        
        // Reset form data
        resetForm();

        handleCloseModal(); // Close the modal after saving changes
      }
    } catch (error) {
      console.error('Error creating new job:', error);
      
      // Show error notification
      setSaveStatus(`Failed to create job: ${error.message}`);
      setShowSaveBanner(true);
      
      // Hide the notification after 5 seconds
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    }
  };

  // Handle confirmation for closing the modal
  const handleCloseWithConfirmation = () => {
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      resetForm();
      handleCloseModal();
    }
  };

  // Handle confirmed close
  const handleConfirmClose = () => {
    resetForm();
    setShowConfirmationModal(false);
    handleCloseModal();
  };

  const renderField = (field, readOnly = false, isDate = false) => {
    // Check if the user can edit this field
    const canUserEditField = canEdit && canEditField(userRoles, field, newJob);
    const isFieldReadOnly = readOnly || !canUserEditField;
    
    if (!isDate) {
      // Check if this is the JobLocation field and provide a custom label
      if (field === 'JobLocation') {
        return (
          <Form.Group className="mb-3">
            <Form.Label>Job Location (City, State)</Form.Label>
            <Form.Control
              type="text"
              id={field}
              name={field}
              value={newJob[field] || ''}
              onChange={handleInputChange}
              readOnly={isFieldReadOnly}
              disabled={isFieldReadOnly}
            />
          </Form.Group>
        );
      }
      
      return (
        <Form.Group className="mb-3">
          <Form.Label>{separateCamelCase(field)}</Form.Label>
          <Form.Control
            type="text"
            id={field}
            name={field}
            value={newJob[field] || ''}
            onChange={handleInputChange}
            readOnly={isFieldReadOnly}
            disabled={isFieldReadOnly}
          />
        </Form.Group>
      );
    }

    return renderDateField(field, isFieldReadOnly);
  };
  const renderDateField = (field, readOnly = false) => {
    // Date field handling
    const dateValue = newJob[field] || '';
    const formattedDate = formatDateForForm(dateValue);
    const isValidDate = isValidDateFormat(dateValue);

    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {separateCamelCase(field)}
        </Form.Label>
        {!isValidDate ? (
          <Form.Control
            type="text"
            id={field}
            name={field}
            value={dateValue}
            onChange={handleInputChange}
            placeholder="Enter date as text"
          />
        ) : (
          <Form.Control
            type="date"
            id={field}
            name={field}
            value={formattedDate}
            onChange={handleInputChange}
            readOnly={readOnly}
            disabled={readOnly}
          />
        )}
      </Form.Group>
    );
  };
  const renderDropdownField = (field, options, displayNameField = null) => {
    const currentValue = newJob[field] || '';
    // Check if the user can edit this field
    const canUserEditField = canEdit && canEditField(userRoles, field, newJob);
    
    return (
      <Form.Group className="mb-3">
        <Form.Label>{separateCamelCase(field)}</Form.Label>
        <Form.Select
          id={field}
          name={field}
          value={currentValue}
          onChange={handleInputChange}
          disabled={!canUserEditField}
        >
          <option value="">Select...</option>
          {options.map((option) => (
            <option 
              key={typeof option === 'string' ? option : option.ID || option.displayName} 
              value={typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            >
              {typeof option === 'string' ? option : (displayNameField ? option[displayNameField] : option.DisplayText)}
            </option>
          ))}
          {!isValueInDropdown(currentValue, options) && currentValue && (
            <option value={currentValue} style={{ color: 'red' }}>
              *{currentValue}
            </option>
          )}
        </Form.Select>
      </Form.Group>
    );
  };

  const formatCurrency = (value) => {
    if (!value) return '';
    // Don't try to format while the user is typing - just return the raw value
    // This prevents NaN from appearing during input
    return value;
  };

  // Function to check if value exists in dropdown options
  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  // Handle opening the engineer assignment modal
  const handleOpenEngineerModal = () => {
    setShowEngineerModal(true);
  };

  // Handle closing the engineer assignment modal
  const handleCloseEngineerModal = () => {
    setShowEngineerModal(false);
  };

  // Handle engineer selection from the modal
  const handleEngineerSelection = (engineerType, engineer, additionalInfo = {}) => {
    console.log('NewJobModal - handleEngineerSelection called with:', { engineerType, engineer: engineer.displayName, additionalInfo });
    
    setNewJob(prevJob => {
      const updatedJob = {
        ...prevJob,
        [engineerType]: engineer.displayName
      };
      
      // Update due date if provided
      if (additionalInfo.dueDateField && additionalInfo.dueDate) {
        updatedJob[additionalInfo.dueDateField] = additionalInfo.dueDate;
        console.log(`NewJobModal - Setting ${additionalInfo.dueDateField} to ${additionalInfo.dueDate}`);
      }
      
      // Update duration if provided
      if (additionalInfo.durationField && additionalInfo.duration) {
        updatedJob[additionalInfo.durationField] = additionalInfo.duration;
        console.log(`NewJobModal - Setting ${additionalInfo.durationField} to ${additionalInfo.duration}`);
      }
      
      console.log('NewJobModal - Updated job:', updatedJob);
      return updatedJob;
    });
    setShowEngineerModal(false);
    
    // Mark that changes have been made
    setChangesMade(true);
  };

  return (
    <>
      {/* Render SaveBanner at the top of the page, outside the modal */}
      {showSaveBanner && <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />}
      <Modal.Header closeButton onHide={handleCloseWithConfirmation}>
        <Modal.Title>Create New Job</Modal.Title>
      </Modal.Header>      <Modal.Body>
        <Form>
          {/* Engineering Requirements Notice - only display if from MO search */}
          {moJobData?.fromMOSearch && <EngineeringRequirementsNotice requirements={engineeringRequirements} />}
          
          {/* Basic Job Information - outside accordion */}
          <h5 className="mb-3 border-bottom pb-2">Basic Job Information</h5>
          <Row>
            <Col md={6}>{renderDropdownField('JobStatus', options.filter(option => option.DropdownType === 'Job Status'))}</Col>
            <Col md={6}>{renderDropdownField('JobType', options.filter(option => option.DropdownType === 'Job Type'))}</Col>
          </Row>
          <Row>
            <Col>{renderField('JobNumber')}</Col>
            <Col>{renderField('ProjectName')}</Col>
            <Col>{renderField('ModelNumber')}</Col>
          </Row>          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Order Received</Form.Label>
                <Form.Control
                  type="date"
                  id="OrderDate"
                  name="OrderDate"
                  value={formatDateForForm(newJob.OrderDate) || ''}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col>{renderField('ProductionRelease', false, true)}</Col>
          </Row><Row>
            <Col>{renderDropdownField('SalesRep', salesOptions, 'displayName')}</Col>
            <Col>{renderField('JobLocation')}</Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Dollar Value</Form.Label>
                <Form.Control
                  type="text"
                  id="DollarValue"
                  name="DollarValue"
                  value={formatCurrency(newJob.DollarValue)}
                  onChange={handleInputChange}
                  readOnly={!canEdit || !canEditField(userRoles, 'DollarValue', newJob)}
                  disabled={!canEdit || !canEditField(userRoles, 'DollarValue', newJob)}
                />
              </Form.Group>
            </Col>
            <Col>{renderField('PaymentTerms')}</Col>
          </Row>
          
          {/* Rest of the form in accordions */}
          <Accordion alwaysOpen>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Engineering</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('MechanicalDue', false, true)}</Col>
                  <Col>{renderField('ElectricalDue', false, true)}</Col>
                  <Col>{renderField('ControlsDue', false, true)}</Col>
                </Row>
                <Row className="mt-3">
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mechanical Duration (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="00"
                        id="MechanicalDuration"
                        name="MechanicalDuration"
                        value={newJob.MechanicalDuration || '1'}
                        onChange={handleInputChange}
                        readOnly={!canEdit || !canEditField(userRoles, 'MechanicalDuration', newJob)}
                        disabled={!canEdit || !canEditField(userRoles, 'MechanicalDuration', newJob)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Electrical Duration (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="00"
                        id="ElectricalDuration"
                        name="ElectricalDuration"
                        value={newJob.ElectricalDuration || '1'}
                        onChange={handleInputChange}
                        readOnly={!canEdit || !canEditField(userRoles, 'ElectricalDuration', newJob)}
                        disabled={!canEdit || !canEditField(userRoles, 'ElectricalDuration', newJob)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Controls Duration (days)</Form.Label>
                      <Form.Control
                        type="number"
                        min="00"
                        id="ControlsDuration"
                        name="ControlsDuration"
                        value={newJob.ControlsDuration || '1'}
                        onChange={handleInputChange}
                        readOnly={!canEdit || !canEditField(userRoles, 'ControlsDuration', newJob)}
                        disabled={!canEdit || !canEditField(userRoles, 'ControlsDuration', newJob)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mechanical Engineer</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          id="MechanicalEngineer"
                          name="MechanicalEngineer"
                          value={newJob.MechanicalEngineer || ''}
                          onChange={handleInputChange}
                          className="flex-grow-1 me-2"
                        >
                          <option value="">Select...</option>
                          {mechOptions.map((option) => (
                            <option key={option.displayName} value={option.displayName}>
                              {option.displayName}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Electrical Engineer</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          id="ElectricalEngineer"
                          name="ElectricalEngineer"
                          value={newJob.ElectricalEngineer || ''}
                          onChange={handleInputChange}
                          className="flex-grow-1 me-2"
                        >
                          <option value="">Select...</option>
                          {elecOptions.map((option) => (
                            <option key={option.displayName} value={option.displayName}>
                              {option.displayName}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Controls Engineer</Form.Label>
                      <div className="d-flex">
                        <Form.Select
                          id="ControlsEngineer"
                          name="ControlsEngineer"
                          value={newJob.ControlsEngineer || ''}
                          onChange={handleInputChange}
                          className="flex-grow-1 me-2"
                        >
                          <option value="">Select...</option>
                          {controlOptions.map((option) => (
                            <option key={option.displayName} value={option.displayName}>
                              {option.displayName}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Button 
                      variant="outline-primary" 
                      className="w-100"
                      onClick={handleOpenEngineerModal}
                    >
                      <i className="bi bi-people-fill"></i> Find Available Engineers
                    </Button>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            
            {/* ... Existing accordions for other sections ... */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Fabrication</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('NPPipeFabDue', false, true)}</Col>
                  <Col>{renderField('FabDue', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="2">
              <Accordion.Header>Control Panel</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('SoftwareDue', false, true)}</Col>
                  <Col>{renderField('ControlPanelDue', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="3">
              <Accordion.Header>Pumps</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('HCPumpsDue', false, true)}</Col>
                  <Col>{renderField('VTPumpsDue', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="4">
              <Accordion.Header>Assembly and Test</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('AssemblyDue', false, true)}</Col>
                  <Col>{renderField('TestDue', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="5">
              <Accordion.Header>NP CSM HDPE</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('NPCSMDue', false, true)}</Col>
                  <Col>{renderField('NPSuctionLineDue', false, true)}</Col>
                </Row>
                <Row>
                  <Col>{renderField('NPInducerTubeDue', false, true)}</Col>
                  <Col>{renderField('NPBMXDue', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="6">
              <Accordion.Header>Shipping</Accordion.Header>
              <Accordion.Body>
                <Row>
                  <Col>{renderField('PackDue', false, true)}</Col>
                  <Col>{renderField('TransportType')}</Col>
                </Row>
                <Row>
                  <Col>{renderField('OriginalScheduledShipDate', false, true)}</Col>
                  <Col>{renderField('CurrentPlanShipDate', false, true)}</Col>
                  <Col>{renderField('ScheduledArrivalDate', false, true)}</Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
          
          {/* Notes section outside accordion */}
          <h5 className="mt-4 mb-3 border-bottom pb-2">Notes</h5>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>{separateCamelCase('Notes')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  id="Notes"
                  name="Notes"
                  value={newJob.Notes || ''}
                  onChange={handleInputChange}
                  placeholder="Enter notes about this job"
                  style={{ resize: "vertical" }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseWithConfirmation}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Create Job
        </Button>
      </Modal.Footer>
      {showEngineerModal && (
        <EngineerAssignmentModal
          show={showEngineerModal}
          onHide={handleCloseEngineerModal}
          onSelectEngineer={handleEngineerSelection}
          engineeringDueDate={newJob.EngineeringDue || ''}
          mechanicalDueDate={newJob.MechanicalDue || ''}
          electricalDueDate={newJob.ElectricalDue || ''}
          controlsDueDate={newJob.ControlsDue || ''}
          mechanicalEngineers={mechOptions}
          electricalEngineers={elecOptions}
          controlsEngineers={controlOptions}
          selectedJob={newJob}
        />
      )}
      
      {/* Confirmation Modal */}
      <Modal className="job-schedule-modal" show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Are you sure you want to close without creating this job?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClose}>
            Close Without Saving
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

NewJobModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  moJobData: PropTypes.object,
  canEdit: PropTypes.bool,
  userRoles: PropTypes.object,
};

export default NewJobModal;
